import { internalLogger } from '../../interface/v1/clientOsLogger';
import bindAllMethods from '../../utils/bindAllMethods';
import { Refreshable } from '../../utils/refreshable/IRefreshable';
import { IContainer, ServiceMethodType, ServicePriority } from './IContainer';

/**
 * Interface for prioritized service methods
 */
interface PrioritizedService {
  method: ServiceMethodType;
  priority: ServicePriority;
  serviceName?: string; // Optional service/class name for better error reporting
}

/**
 * Base container implementation that manages service initialization and refresh operations.
 * Supports prioritized execution of services.
 */
export default class BaseContainer implements IContainer {
  private _initContextServices: PrioritizedService[];
  private _refreshContextServices: PrioritizedService[];

  /**
   * Creates a new BaseContainer instance
   */
  constructor() {
    this._initContextServices = [];
    this._refreshContextServices = [];
    bindAllMethods(this);
  }

  /**
   * Adds an initialization method to the container with the specified priority.
   * @param serviceInitMethod - The service initialization method to add
   * @param priority - The priority level for execution (default: MEDIUM)
   * @param serviceName - Optional name of the service/class for better error reporting
   */
  public addInitServices(
    serviceInitMethod: ServiceMethodType,
    priority: ServicePriority = ServicePriority.MEDIUM,
    serviceName?: string
  ): void {
    this._initContextServices.push({
      method: serviceInitMethod,
      priority,
      serviceName
    });
  }

  /**
   * Adds a refresh method to the container with the specified priority.
   * @param serviceRefreshMethod - The service refresh method to add
   * @param priority - The priority level for execution (default: MEDIUM)
   * @param serviceName - Optional name of the service/class for better error reporting
   */
  public addRefreshServices(
    serviceRefreshMethod: ServiceMethodType,
    priority: ServicePriority = ServicePriority.MEDIUM,
    serviceName?: string
  ): void {
    this._refreshContextServices.push({
      method: serviceRefreshMethod,
      priority,
      serviceName
    });
  }

  /**
   * Adds a Refreshable service to the container with the specified priority.
   * Both init and refresh methods will be added with the same priority.
   * @param service - The Refreshable service to add
   * @param priority - The priority level for execution (default: MEDIUM)
   */
  public addService(
    service: Refreshable,
    priority: ServicePriority = ServicePriority.MEDIUM
  ): void {
    const { init, refresh } = service.getRefreshMethods();
    const serviceName = service.constructor.name;
    this.addInitServices(init, priority, serviceName);
    this.addRefreshServices(refresh, priority, serviceName);
  }

  /**
   * Executes service methods in order of priority (highest to lowest).
   * Methods with the same priority are executed concurrently.
   * @param services - Array of prioritized services
   * @returns Promise that resolves when all services are executed
   */
  private async executeByPriority(
    services: PrioritizedService[]
  ): Promise<void> {
    // Sort services by priority (highest to lowest)
    const sortedByPriority = [...services].sort(
      (a, b) => b.priority - a.priority
    );

    // Group services by priority level
    const priorityGroups = new Map<ServicePriority, PrioritizedService[]>();

    sortedByPriority.forEach((service) => {
      if (!priorityGroups.has(service.priority)) {
        priorityGroups.set(service.priority, []);
      }
      priorityGroups.get(service.priority)!.push(service);
    });

    // Execute each priority group in sequence (highest priority first)
    const priorityLevels = Array.from(priorityGroups.keys()).sort(
      (a, b) => b - a
    );

    for (const priorityLevel of priorityLevels) {
      const services = priorityGroups.get(priorityLevel)!;

      // Execute all methods of the same priority concurrently
      await Promise.all(
        services.map(async (service) => {
          try {
            internalLogger?.debug?.(
              '[Refreshable Container] Refreshing service ' +
                service.serviceName +
                ' with priorityLevel ' +
                priorityLevel
            );
            await service.method();
            internalLogger?.debug?.(
              '[Refreshable Container] Service ' +
                service.serviceName +
                ' executed. '
            );
          } catch (methodError) {
            const serviceName = service.serviceName || 'unknown service';
            internalLogger?.error?.(
              `[Refreshable Container] Error occurred when executing method "${service.method.name}" from service "${serviceName}" with priority ${priorityLevel}:`,
              methodError
            );
          }
        })
      );
      // Continue to lower priority services despite errors in higher priority ones
    }
  }

  /**
   * Initializes all registered services based on their priority.
   * Higher priority services are initialized before lower priority ones.
   * Services with the same priority are initialized concurrently.
   * @returns A Promise that resolves when all services are initialized
   */
  public async initializeContextServices(): Promise<void> {
    try {
      await this.executeByPriority(this._initContextServices);
    } catch (error) {
      internalLogger?.error?.('Failed to initialize context services:', error);
    }
  }

  /**
   * Refreshes all registered services based on their priority.
   * Higher priority services are refreshed before lower priority ones.
   * Services with the same priority are refreshed concurrently.
   * @returns A Promise that resolves when all services are refreshed
   */
  public async refreshContext(): Promise<void> {
    try {
      await this.executeByPriority(this._refreshContextServices);
    } catch (error) {
      internalLogger?.error?.('Failed to refresh context services:', error);
    }
  }
}
